/**
 * O símbolo EAN-13 desenhado em SVG.
 *
 * SVG e não `<canvas>` nem imagem: a impressão do navegador rasteriza vetor na
 * resolução da impressora, e é isso que faz a barra sair com borda limpa em
 * 600 dpi. Um canvas de 200 px de largura imprimiria os mesmos 200 px
 * borrados, e é aí que o leitor começa a errar.
 *
 * ## As medidas não são estéticas
 *
 * A norma define o módulo (a barra mais fina) em 0,33 mm no fator 1,0, e
 * aceita de 0,8 a 2,0 desse valor. O símbolo tem 95 módulos, então
 * 95 × 0,33 ≈ 31,4 mm de largura — fixa, e não "100 % da etiqueta". Esticar o
 * símbolo até a largura da etiqueta produziria módulo de 1 mm na folha de
 * 2 × 7, fora da faixa da norma: o leitor até costuma ler, e "costuma" não é o
 * que se quer no caixa de sábado.
 */
import { barrasEAN13, legivelEAN13 } from './codigo-de-barras'

/** 95 módulos × 0,33 mm. */
export const LARGURA_SIMBOLO_MM = 31.4

/**
 * O sistema de coordenadas interno do SVG é em MÓDULOS na horizontal — as
 * barras já vêm medidas assim, e converter para milímetro aqui dentro só
 * introduziria arredondamento entre uma barra e a vizinha.
 */
const ALTURA_EM_UNIDADES = 30

export function CodigoDeBarras({
  codigo,
  alturaMM = 8,
  comNumero = true,
}: {
  codigo: string
  alturaMM?: number
  comNumero?: boolean
}) {
  const barras = barrasEAN13(codigo)

  // Código que não é EAN-13 válido não vira símbolo nenhum — ver
  // `modulosEAN13`. Sobra o número, que ao menos pode ser digitado à mão.
  if (!barras) {
    return <div className="tabular text-[6pt] tracking-wide">{codigo}</div>
  }

  return (
    <div>
      <svg
        role="img"
        aria-label={`Código de barras ${legivelEAN13(codigo)}`}
        width={`${LARGURA_SIMBOLO_MM}mm`}
        height={`${alturaMM}mm`}
        viewBox={`0 0 95 ${ALTURA_EM_UNIDADES}`}
        preserveAspectRatio="none"
        shapeRendering="crispEdges"
      >
        {barras.map((barra) => (
          <rect
            key={barra.inicio}
            x={barra.inicio}
            y={0}
            width={barra.largura}
            height={ALTURA_EM_UNIDADES}
            fill="#000000"
          />
        ))}
      </svg>
      {comNumero && (
        <div
          className="tabular text-[5.5pt] leading-none tracking-[0.08em] text-black"
          style={{ width: `${LARGURA_SIMBOLO_MM}mm`, textAlign: 'center' }}
        >
          {legivelEAN13(codigo)}
        </div>
      )}
    </div>
  )
}
